import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import { message } from 'antd'

import requests from '../../axios/requests'
import { setUser } from '../../../redux/actions/userActions'
import { setConfig, setLoadingGpt, setLoadingIndx, setSelectedTag } from '../../../redux/actions/actions'
import variableLanguage from '../../utils/hooks/hookGetVariablesLanguage'

import useTrajectory from '../SkillSet/gen/useTrajectory'
import { PlanCard, PlanEmpty, PlanGenerating, PlanModal } from '../SkillSet/gen/PlanCard'
import { pluralN, skillStatus } from '../SkillSet/gen/genUtils'
import { docName } from '../SkillSet/gen/DocSource'
import TestGen, { KindPicker } from '../SkillSet/gen/TestGen'
import LangFlag, { LANG_NAME } from '../SkillSet/gen/LangFlag'
import s from '../SkillSet/gen/gen.module.scss'
import p from '../SkillSet/gen/plans.module.scss'

const LANGS = ['RU', 'EN', 'SA']

const GenerateCourseSkillSet = () => {
  const variable_15 = variableLanguage({ keyPage: 'generateCourse', keyVariable: 'variable_15' })

  const history = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { user, config, selectedTag, selectedDocument, loadingIndx } = useSelector((state) => state.Reducer)
  const trajectory = useTrajectory()
  // источник из оболочки ИИ-генератора; вне неё — только по навыку
  const outlet = useOutletContext() || {}
  const byDoc = outlet.source === 'doc'
  const setSource = outlet.setSource || (() => {})

  const slots = Number(config?.PLAN_GENERATION_UPDATE_LIMIT) || 4
  const [plans, setPlans] = useState([])
  const [lang, setLang] = useState('RU')
  const [creatingId, setCreatingId] = useState(null)
  const [modalPlan, setModalPlan] = useState(null)
  const [favPage, setFavPage] = useState(false)
  const [favorites, setFavorites] = useState({ results: [], count: 0 })
  const [favTag, setFavTag] = useState(null)
  const [favCurrent, setFavCurrent] = useState(1)
  const [docPlans, setDocPlans] = useState([])
  const [docGenerating, setDocGenerating] = useState(false)
  const pollRef = useRef(null)
  // «Что создаём»: курс или только тест. Запоминаем выбор; «+ Итоговый тест» с шага 3 открывает тест.
  const [kind, setKindState] = useState(() => {
    if (location.state?.kind) return location.state.kind
    try {
      return localStorage.getItem('gen_kind') === 'test' ? 'test' : 'course'
    } catch (e) {
      return 'course'
    }
  })
  const setKind = (value) => {
    setKindState(value)
    try {
      localStorage.setItem('gen_kind', value)
    } catch (e) {}
  }

  const limit = user?.education_plan_limit || 0
  const generating = loadingIndx !== null && loadingIndx !== undefined

  const fetchPlans = (tag = selectedTag) => {
    if (!tag?.id) return Promise.resolve()
    return requests.users
      .fetch_education_plan(tag.id)
      .then((res) => {
        const data = res.data?.results || []
        setPlans(data)
        return data
      })
      .catch((err) => console.error(err))
  }

  const fetchProfile = () =>
    requests.users
      .get_profile_new()
      .then((res) => dispatch(setUser(res.data)))
      .catch((err) => console.error(err))

  // прогресс celery-задачи генерации плана
  const waitTask = (taskId) => {
    requests.users
      .calery_progress(taskId)
      .then((res) => {
        if (res.data?.progress?.current !== 100) {
          pollRef.current = setTimeout(() => waitTask(taskId), 2000)
          return
        }
        fetchPlans().then(() => {
          dispatch(setLoadingGpt(false))
          dispatch(setLoadingIndx(null))
          trajectory.refresh()
        })
      })
      .catch((err) => {
        console.error(err)
        dispatch(setLoadingGpt(false))
        dispatch(setLoadingIndx(null))
      })
  }

  const generatePlan = () => {
    if (!selectedTag?.id || generating) return
    if (plans.length === 0 && limit === 0) return message.error(variable_15)
    dispatch(setLoadingIndx(plans.length))
    dispatch(setLoadingGpt(true))
    requests.users
      .education_plan({ speciality_tag: selectedTag.id, language: lang.toLowerCase() })
      .then((res) => waitTask(res.data.task_id))
      .catch((err) => {
        dispatch(setLoadingGpt(false))
        dispatch(setLoadingIndx(null))
        const data = err?.response?.data
        if (Array.isArray(data)) data.forEach((m) => message.error(m))
        else message.error('Не удалось запустить генерацию плана')
      })
  }

  const createCourse = (plan) => {
    if (limit === 0) return message.error(variable_15)
    setCreatingId(plan.id)
    requests.users
      .generate_quest(plan.id)
      .then((res) => {
        dispatch(setUser({ ...user, education_plan_limit: Math.max(0, limit - 1) }))
        trajectory.refresh()
        const ses = res.data?.quest?.ses
        if (ses) history(`/backoffice/view-course/${ses}`)
        else fetchPlans()
      })
      .catch((err) => {
        console.error(err)
        message.error(err?.response?.data?.detail || 'Не удалось создать курс')
      })
      .finally(() => setCreatingId(null))
  }

  const toggleFavorite = (plan) =>
    requests.users
      .changeFavoritePlan(plan.id, !plan.favorite)
      .then(() => {
        setPlans((prev) => prev.map((x) => (x.id === plan.id ? { ...x, favorite: !plan.favorite } : x)))
        if (favPage) fetchFavorites()
        trajectory.refresh()
      })
      .catch((err) => console.error(err))

  const fetchFavorites = () =>
    requests.users
      .fetchFavorites(favCurrent, slots, favTag)
      .then(({ data }) => setFavorites(data || { results: [], count: 0 }))
      .catch((err) => console.error(err))

  const openCourse = (plan) => history(`/backoffice/view-course/${plan.quest.ses}`)

  // ── по документу: планы из загруженного файла (каждый вариант и курс списывают 1 генерацию) ──
  const fetchDocPlans = (doc = selectedDocument) => {
    if (!doc?.id) return Promise.resolve()
    return requests.users
      .get_education_plan_pdf_list(doc.id)
      .then((res) => {
        const raw = res.data
        const all = Array.isArray(raw) ? raw : Array.isArray(raw?.results) ? raw.results : raw?.id ? [raw] : []
        setDocPlans(all.filter((x) => String(x.pdf ?? x.pdf_id ?? x.education_pdf) === String(doc.id)))
      })
      .catch((err) => console.error(err))
  }

  const generateDocPlan = () => {
    if (!selectedDocument?.id || docGenerating) return
    if (limit === 0) return message.error(variable_15)
    setDocGenerating(true)
    requests.users
      .create_education_plan_from_pdf_and_wait(selectedDocument.id, lang.toLowerCase())
      .then(() => {
        fetchProfile()
        return fetchDocPlans()
      })
      .catch((err) => {
        const data = err?.response?.data
        message.error(
          (Array.isArray(data) && data[0]) || data?.error || data?.message || err.message || 'Ошибка создания плана'
        )
      })
      .finally(() => setDocGenerating(false))
  }

  const createDocCourse = (plan) => {
    if (limit === 0) return message.error(variable_15)
    setCreatingId(plan.id)
    requests.users
      .generate_quest_pdf(plan.id)
      .then((res) => {
        dispatch(setUser({ ...user, education_plan_limit: Math.max(0, limit - 1) }))
        const ses = res.data?.quest?.ses
        if (ses) history(`/backoffice/view-course/${ses}`)
        else fetchDocPlans()
      })
      .catch((err) => {
        console.error(err)
        message.error(err?.response?.data?.detail || 'Не удалось создать курс')
      })
      .finally(() => setCreatingId(null))
  }

  const toggleDocFavorite = (plan) =>
    requests.users
      .changeFavoritePlan(plan.id, !plan.favorite)
      .then(() => setDocPlans((prev) => prev.map((x) => (x.id === plan.id ? { ...x, favorite: !plan.favorite } : x))))
      .catch((err) => console.error(err))

  useEffect(() => {
    fetchProfile()
    trajectory.refresh()
    requests.users
      .get_config()
      .then((res) => dispatch(setConfig(res.data)))
      .catch((err) => console.error(err))
    return () => clearTimeout(pollRef.current)
  }, [])

  // навык по умолчанию: первый без курса
  useEffect(() => {
    if (byDoc || selectedTag?.id || !trajectory.list.length) return
    const first = trajectory.list.find((x) => skillStatus(x).kind !== 'course') || trajectory.list[0]
    if (first && typeof first.id === 'number') dispatch(setSelectedTag(first))
  }, [trajectory.list, selectedTag, byDoc])

  useEffect(() => {
    setPlans([])
    if (selectedTag?.id) fetchPlans(selectedTag)
  }, [selectedTag])

  // если план генерировался, пока страница была закрыта, — подтягиваем его, когда он будет готов
  useEffect(() => {
    if (generating || !plans.some((x) => x.generation_in_progress)) return
    const t = setTimeout(() => fetchPlans(), 4000)
    return () => clearTimeout(t)
  }, [plans, generating])

  useEffect(() => {
    if (favPage) fetchFavorites()
  }, [favPage, favCurrent, favTag])

  useEffect(() => {
    setDocPlans([])
    if (selectedDocument?.id) fetchDocPlans(selectedDocument)
  }, [selectedDocument])

  useEffect(() => {
    if (docGenerating || !docPlans.some((x) => x.generation_in_progress)) return
    const t = setTimeout(() => fetchDocPlans(), 4000)
    return () => clearTimeout(t)
  }, [docPlans, docGenerating])

  const langSeg = (
    <div className={s.seg} role="group" aria-label="Язык плана">
      {LANGS.map((l) => (
        <button
          type="button"
          key={l}
          className={cn({ [s.segOn]: lang === l })}
          aria-pressed={lang === l}
          onClick={() => setLang(l)}
          title={LANG_NAME[l]}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <LangFlag lang={l} />
          {l}
        </button>
      ))}
    </div>
  )

  const docTitle = selectedDocument ? docName(selectedDocument).replace(/\.[a-z0-9]+$/i, '') : ''
  const subjectName = byDoc ? docTitle : selectedTag?.name || ''
  const stageHead = (
    <div className={s.stagehead}>
      <div>
        <div className={s.kicker}>Шаг 2 из 3{subjectName && !favPage ? ` · ${subjectName}` : ''}</div>
        <h2>{favPage ? 'Избранные планы' : 'Что создаём?'}</h2>
        <div className={cn(s.muted, s.small)}>
          {favPage
            ? 'Планы, которые вы отметили звёздочкой, по всем навыкам'
            : kind === 'test'
            ? byDoc
              ? 'ИИ составит тест по содержанию загруженного файла'
              : 'ИИ подберёт темы навыка и вопросы с правильными ответами'
            : byDoc
            ? 'ИИ возьмёт структуру и темы из загруженного файла'
            : 'ИИ предложит до четырёх вариантов плана по выбранному навыку'}
        </div>
      </div>
      {!favPage && outlet.setSource && (
        <div className={p.lbl}>
          Из чего
          <div className={s.seg} role="group" aria-label="Источник генерации">
            {[
              ['skill', 'По навыку'],
              ['doc', 'По документу'],
            ].map(([key, label]) => (
              <button
                type="button"
                key={key}
                className={cn({ [s.segOn]: outlet.source === key })}
                aria-pressed={outlet.source === key}
                onClick={() => setSource(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  const kindPicker = <KindPicker value={kind} onChange={setKind} />
  if (kind === 'test' && !favPage) {
    const ready = byDoc ? !!selectedDocument?.id : trajectory.list.length > 0 && !!selectedTag?.id
    return (
      <section className={s.stage} aria-label="Генерация теста">
        {stageHead}
        {kindPicker}
        {byDoc && !selectedDocument?.id ? (
          <div className={cn(s.card, s.empty)}>
            Выберите документ в зоне «Документ-источник» выше или загрузите новый — программу, методичку, презентацию
          </div>
        ) : !byDoc && !trajectory.list.length ? (
          <div className={cn(s.card, s.empty)}>
            <h3 style={{ marginBottom: 6 }}>Учебный план пока пуст</h3>
            <div style={{ marginBottom: 14 }}>
              Добавьте навыки на шаге «Обзор профессий» или через поле в учебном плане
            </div>
            <button
              type="button"
              className={cn(s.btn, s.pri)}
              onClick={() => history('/backoffice/skill-set/prof-review')}
            >
              К обзору профессий
            </button>
          </div>
        ) : !ready ? (
          <div className={cn(s.card, s.empty)}>Выберите навык в учебном плане выше — подготовим тест по нему</div>
        ) : (
          <TestGen
            byDoc={byDoc}
            tag={selectedTag}
            doc={selectedDocument}
            docTitle={docTitle}
            limit={limit}
            slots={slots}
            onBalance={() => {
              fetchProfile()
              trajectory.refresh()
            }}
            onCreated={(draft, openOnly) => {
              if (openOnly) return history(`/backoffice/current-course/${draft.quest.ses}`)
              history('/backoffice/skill-set/courses', { state: { createdTest: draft } })
            }}
          />
        )}
      </section>
    )
  }
  if (favPage) {
    const favSkills = trajectory.list.filter((x) => x.favorite)
    const pages = Math.max(1, Math.ceil((favorites.count || 0) / slots))
    return (
      <section className={s.stage} aria-label="Избранные планы">
        {stageHead}
        <div className={p.head}>
          <button type="button" className={cn(s.btn, s.ghost, s.sm)} onClick={() => setFavPage(false)}>
            ← К вариантам
          </button>
          {favSkills.length > 0 && (
            <div className={p.favSkills} role="group" aria-label="Фильтр по навыку">
              {favSkills.map((sk) => (
                <button
                  type="button"
                  key={sk.id}
                  className={cn(s.pchip, s.pchipBtn, { [s.pchipOn]: favTag === sk.id })}
                  aria-pressed={favTag === sk.id}
                  onClick={() => {
                    setFavCurrent(1)
                    setFavTag(favTag === sk.id ? null : sk.id)
                  }}
                >
                  {sk.name}
                </button>
              ))}
            </div>
          )}
        </div>
        {favorites.results?.length ? (
          <div className={p.grid}>
            {favorites.results.map((plan, i) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                title={`Избранный план ${(favCurrent - 1) * slots + i + 1}`}
                busy={creatingId !== null}
                onCreate={() => createCourse(plan)}
                onOpenCourse={() => openCourse(plan)}
                onFavorite={() => toggleFavorite(plan)}
                onExpand={() => setModalPlan({ plan, title: 'Избранный план' })}
                createNote="Спишется 1 генерация"
              />
            ))}
          </div>
        ) : (
          <div className={cn(s.card, s.empty)}>
            Пока нет избранных планов — отметьте понравившийся вариант звёздочкой
          </div>
        )}
        {pages > 1 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
            <button
              type="button"
              className={cn(s.btn, s.ghost, s.sm)}
              disabled={favCurrent <= 1}
              onClick={() => setFavCurrent(favCurrent - 1)}
              aria-label="Предыдущая страница"
            >
              ‹
            </button>
            <span className={cn(s.muted, s.small)}>
              {favCurrent} из {pages}
            </span>
            <button
              type="button"
              className={cn(s.btn, s.ghost, s.sm)}
              disabled={favCurrent >= pages}
              onClick={() => setFavCurrent(favCurrent + 1)}
              aria-label="Следующая страница"
            >
              ›
            </button>
          </div>
        )}
        <PlanModal
          open={!!modalPlan}
          plan={modalPlan?.plan}
          title={modalPlan?.title}
          onClose={() => setModalPlan(null)}
          footer={
            modalPlan?.plan && !modalPlan.plan.quest?.id ? (
              <button
                type="button"
                className={cn(s.btn, s.pri)}
                disabled={creatingId !== null}
                onClick={() => createCourse(modalPlan.plan)}
              >
                Создать курс по этому плану
              </button>
            ) : null
          }
        />
      </section>
    )
  }

  if (byDoc) {
    const ready = docPlans.filter((x) => !x.generation_in_progress).length
    const title = docTitle
    return (
      <section className={s.stage} aria-label="Генерация курса по документу">
        {stageHead}
        {kindPicker}
        {!selectedDocument?.id ? (
          <div className={cn(s.card, s.empty)}>
            Выберите документ в зоне «Документ-источник» выше или загрузите новый — программу, методичку, презентацию
          </div>
        ) : (
          <>
            <div className={cn(s.card, p.head)}>
              <div>
                <div className={cn(s.muted, s.small)}>
                  Планы обучения по документу · {ready} из {pluralN(slots, ['варианта', 'вариантов', 'вариантов'])}
                </div>
                <h2>{title}</h2>
              </div>
              <div className={p.headRight}>
                <span className={cn(s.muted, s.small)}>Язык нового плана</span>
                {langSeg}
              </div>
            </div>
            <div className={p.grid}>
              {Array.from({ length: slots }).map((_, i) => {
                const plan = docPlans[i]
                const t = `Вариант ${i + 1}`
                if (plan && plan.generation_in_progress) return <PlanGenerating key={plan.id} title={t} />
                if (plan)
                  return (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      title={t}
                      busy={creatingId !== null || docGenerating}
                      onCreate={() => createDocCourse(plan)}
                      onOpenCourse={() => openCourse(plan)}
                      onFavorite={() => toggleDocFavorite(plan)}
                      onExpand={() => setModalPlan({ plan, title: t, doc: true })}
                      createNote={creatingId === plan.id ? 'Создаём курс…' : 'Спишется 1 генерация'}
                    />
                  )
                if (docGenerating && i === docPlans.length)
                  return <PlanGenerating key={`g${i}`} title={t} lang={lang} />
                return (
                  <PlanEmpty
                    key={`e${i}`}
                    title={t}
                    disabled={docGenerating || i !== docPlans.length || limit === 0}
                    onGenerate={generateDocPlan}
                    note={
                      i !== docPlans.length
                        ? 'Станет доступен после предыдущего варианта'
                        : limit === 0
                        ? 'Пополните баланс генераций'
                        : 'Спишется 1 генерация'
                    }
                  />
                )
              })}
            </div>
          </>
        )}
        <PlanModal
          open={!!modalPlan}
          plan={modalPlan?.plan}
          title={modalPlan?.title}
          onClose={() => setModalPlan(null)}
          footer={
            modalPlan?.plan && !modalPlan.plan.quest?.id ? (
              <button
                type="button"
                className={cn(s.btn, s.pri)}
                disabled={creatingId !== null}
                onClick={() => createDocCourse(modalPlan.plan)}
              >
                Создать курс по этому плану
              </button>
            ) : null
          }
        />
      </section>
    )
  }

  const readyCount = plans.filter((x) => !x.generation_in_progress).length

  return (
    <section className={s.stage} aria-label="Генерация курса">
      {stageHead}
      {kindPicker}

      {!trajectory.list.length ? (
        <div className={cn(s.card, s.empty)}>
          <h3 style={{ marginBottom: 6 }}>Учебный план пока пуст</h3>
          <div style={{ marginBottom: 14 }}>
            Добавьте навыки на шаге «Обзор профессий» или через поле в учебном плане
          </div>
          <button
            type="button"
            className={cn(s.btn, s.pri)}
            onClick={() => history('/backoffice/skill-set/prof-review')}
          >
            К обзору профессий
          </button>
        </div>
      ) : !selectedTag?.id ? (
        <div className={cn(s.card, s.empty)}>Выберите навык в учебном плане выше — покажем варианты плана по нему</div>
      ) : (
        <>
          <div className={cn(s.card, p.head)}>
            <div>
              <div className={cn(s.muted, s.small)}>
                Планы обучения по навыку · {readyCount} из {pluralN(slots, ['варианта', 'вариантов', 'вариантов'])}
              </div>
              <h2>{selectedTag.name}</h2>
            </div>
            <div className={p.headRight}>
              <span className={cn(s.muted, s.small)}>Язык нового плана</span>
              {langSeg}
              <button type="button" className={cn(s.btn, s.ghost, s.sm)} onClick={() => setFavPage(true)}>
                ★ Избранные планы
              </button>
            </div>
          </div>

          <div className={p.grid}>
            {Array.from({ length: slots }).map((_, i) => {
              const plan = plans[i]
              const title = `Вариант ${i + 1}`
              if (plan && plan.generation_in_progress) return <PlanGenerating key={plan.id} title={title} />
              if (plan)
                return (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    title={title}
                    busy={creatingId !== null || generating}
                    onCreate={() => createCourse(plan)}
                    onOpenCourse={() => openCourse(plan)}
                    onFavorite={() => toggleFavorite(plan)}
                    onExpand={() => setModalPlan({ plan, title })}
                    createNote={creatingId === plan.id ? 'Создаём курс…' : 'Спишется 1 генерация'}
                  />
                )
              if (generating && i === loadingIndx) return <PlanGenerating key={`g${i}`} title={title} lang={lang} />
              return (
                <PlanEmpty
                  key={`e${i}`}
                  title={title}
                  disabled={generating || i !== plans.length}
                  onGenerate={generatePlan}
                  note={
                    i !== plans.length
                      ? 'Станет доступен после предыдущего варианта'
                      : plans.length === 0 && limit === 0
                      ? 'Нужна хотя бы 1 генерация на балансе'
                      : 'План бесплатный — генерация спишется при создании курса'
                  }
                />
              )
            })}
          </div>
        </>
      )}

      <PlanModal
        open={!!modalPlan}
        plan={modalPlan?.plan}
        title={modalPlan?.title}
        onClose={() => setModalPlan(null)}
        footer={
          modalPlan?.plan && !modalPlan.plan.quest?.id ? (
            <button
              type="button"
              className={cn(s.btn, s.pri)}
              disabled={creatingId !== null}
              onClick={() => createCourse(modalPlan.plan)}
            >
              Создать курс по этому плану
            </button>
          ) : null
        }
      />
    </section>
  )
}

export default GenerateCourseSkillSet
